<template>
  <div>
    <!-- 使用公共导航栏组件 -->
    <Nav />

    <!-- 主要内容区域 -->
    <main class="mt-20 min-h-screen">
      <!-- 页面标题区域 -->
      <section class="bg-white py-8 border-b border-gray-200">
        <div class="max-w-7xl mx-auto px-6">
          <div class="flex items-center justify-between">
            <div>
              <h1 class="text-3xl font-bold text-gray-900 mb-2">案例分享</h1>
              <p class="text-gray-600">
                探索成功案例，了解会展设备租赁的实际应用效果
              </p>
            </div>
            <div class="flex items-center space-x-4">
              <div
                class="flex items-center space-x-2 px-1 py-1 bg-gray-100 rounded-full"
              >
                <button
                  class="px-4 py-2 rounded-full font-medium text-sm whitespace-nowrap view-toggle"
                  :class="
                    viewMode === 'grid'
                      ? 'bg-white text-primary active'
                      : 'text-gray-600'
                  "
                  @click="switchToGridView"
                >
                  <i class="ri-grid-line mr-1"></i>网格视图
                </button>
                <button
                  class="px-4 py-2 rounded-full font-medium text-sm whitespace-nowrap view-toggle"
                  :class="
                    viewMode === 'list'
                      ? 'bg-white text-primary active'
                      : 'text-gray-600'
                  "
                  @click="switchToListView"
                >
                  <i class="ri-list-check mr-1"></i>列表视图
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div class="max-w-7xl mx-auto px-6 py-8">
        <div class="flex gap-8">
          <!-- 左侧筛选区域 -->
          <aside
            class="w-80 bg-white rounded-xl shadow-sm p-6 h-fit sticky top-28"
          >
            <!-- 搜索框 -->
            <div class="mb-6">
              <div class="relative">
                <input
                  type="text"
                  v-model="searchKeyword"
                  placeholder="搜索案例名称或关键词"
                  class="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  @keyup.enter="handleSearch"
                />
                <div
                  class="absolute left-3 top-3 w-4 h-4 flex items-center justify-center"
                >
                  <i class="ri-search-line text-gray-400"></i>
                </div>
                <button
                  class="absolute right-3 top-2 bg-primary text-white px-3 py-1 rounded-md text-sm"
                  @click="handleSearch"
                >
                  搜索
                </button>
              </div>
            </div>
            <!-- 行业类型筛选 -->
            <div class="mb-6">
              <h3 class="font-semibold text-gray-900 mb-4">行业分类</h3>
              <div class="space-y-3" id="industry-filter-container">
                <!-- 全部分类选项 -->
                <label class="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    class="industry-filter sr-only"
                    value="all"
                    :checked="selectedCategories.includes('all')"
                    @change="(e) => handleCategorySelect(e, 'all')"
                  />
                  <div
                    class="w-5 h-5 flex items-center justify-center mr-3 custom-checkbox rounded"
                    :class="
                      selectedCategories.includes('all')
                        ? 'bg-primary border-primary'
                        : 'border-2 border-gray-300 rounded'
                    "
                  >
                    <i
                      class="ri-check-line text-white text-xs"
                      :class="{ hidden: !selectedCategories.includes('all') }"
                    ></i>
                  </div>
                  <span class="text-sm text-gray-700 industry-text"
                    >全部行业</span
                  >
                  <span class="ml-auto text-xs text-gray-500"
                    >({{ totalCount || 0 }})</span
                  >
                </label>

                <!-- 动态渲染行业分类 -->
                <template v-if="categoryList && categoryList.length > 0">
                  <label
                    v-for="category in categoryList"
                    :key="category.id"
                    class="flex items-center cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      class="industry-filter sr-only"
                      :value="category.id"
                      :checked="selectedCategories.includes(category.id)"
                      @change="(e) => handleCategorySelect(e, category.id)"
                    />
                    <div
                      class="w-5 h-5 flex items-center justify-center mr-3 custom-checkbox rounded"
                      :class="
                        selectedCategories.includes(category.id)
                          ? 'bg-primary border-primary '
                          : 'border-2 border-gray-300'
                      "
                    >
                      <i
                        class="ri-check-line text-white text-xs"
                        :class="{
                          hidden: !selectedCategories.includes(category.id),
                        }"
                      ></i>
                    </div>
                    <span class="text-sm text-gray-700 industry-text">{{
                      category.name
                    }}</span>
                    <span class="ml-auto text-xs text-gray-500"
                      >({{ category.case_count || 0 }})</span
                    >
                  </label>
                </template>

                <!-- 加载中状态 -->
                <div
                  v-else-if="loading"
                  class="text-center py-2 text-sm text-gray-500"
                >
                  加载中...
                </div>

                <!-- 加载失败状态 -->
                <div v-else class="text-center py-2 text-sm text-gray-500">
                  暂无行业分类数据
                </div>
              </div>
            </div>
            <!-- 重置筛选 -->
            <button
              class="w-full bg-gray-100 text-gray-700 py-3 !rounded-button hover:bg-gray-200 transition-colors whitespace-nowrap"
              id="reset-filters"
              @click="resetFilters"
            >
              重置筛选
            </button>
          </aside>
          <!-- 右侧案例列表区域 -->
          <div class="flex-1">
            <!-- 排序和结果统计 -->
            <div class="bg-white rounded-xl shadow-sm p-4 mb-6">
              <div class="flex items-center justify-between">
                  <div class="text-sm text-gray-600">
                    共找到
                    <span class="text-primary font-medium">{{
                      totalCount || 0
                    }}</span>
                    个成功案例
                  </div>
                  <div class="flex items-center space-x-4">
                    <span class="text-sm text-gray-600">排序：</span>
                    <div class="relative">
                      <button
                        id="sortBtn"
                        class="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-w-[120px]"
                        @click="toggleSortDropdown"
                      >
                        <span>{{ sortOptions[sortBy] || "默认排序" }}</span>
                        <i class="ri-arrow-down-s-line ml-2"></i>
                      </button>
                      <div
                        id="sortDropdown"
                        v-if="showSortDropdown"
                        class="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10"
                      >
                        <div
                          v-for="(label, value) in sortOptions"
                          :key="value"
                          class="px-4 py-2 text-sm cursor-pointer hover:bg-gray-50"
                          :class="{ 'text-primary': sortBy === value }"
                          @click="handleSortChange(value)"
                        >
                          {{ label }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
            </div>
            <!-- 案例网格/列表 -->
            <div
              id="case-grid"
              :class="{
                'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6': viewMode === 'grid',
                'space-y-4': viewMode === 'list'
              }"
            >
              <!-- 案例卡片 -->
              <div
                class="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                :class="{
                  'case-card': viewMode === 'grid',
                  'flex': viewMode === 'list'
                }"
                data-industry="exhibition"
                data-equipment="audio,construction"
                data-scale="large"
                v-for="item in caseList"
                :key="item.id"
                @click="goToCaseDetail(item.id)"
              >
                <div :class="{ 'w-full': viewMode === 'grid', 'w-1/3': viewMode === 'list' }">
                  <div class="relative group">
                    <img
                      :src="item.thumbnail_url"
                      class="w-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                       :style="{ height: viewMode === 'list' ? '200px' : '200px' }"
                    />
                    <span
                      class="absolute top-3 left-3 bg-primary text-white px-2 py-1 text-xs rounded"
                      >{{ item.category_name }}</span
                    >
                    <span
                      class="absolute top-3 right-3 bg-red-500 text-white px-2 py-1 text-xs rounded"
                      v-if="item.is_recommend == 1"
                      >热门</span
                    >
                  </div>
                </div>
                <div :class="{ 'w-full p-6': viewMode === 'grid', 'w-2/3 p-6 flex flex-col justify-between': viewMode === 'list' }">
                  <h3 class="text-xl font-semibold text-gray-900 mb-2">
                    {{ item.title }}
                  </h3>

                  <p class="text-gray-600 text-sm mb-4 line-clamp-2">
                    {{ item.description }}
                  </p>
                  <div class="mb-4">
                    <h4 class="text-sm font-medium text-gray-900 mb-2">
                      主要设备清单
                    </h4>
                    <div class="flex flex-wrap gap-2">
                      <!-- 遍历显示主要设备清单 随机背景颜色 -->
                      <span
                        v-for="(equipment, index) in item.item_list"
                        :key="index"
                        :class="[
                          'inline-flex items-center px-2 py-1 text-xs rounded',
                          getRandomColorClass(),
                        ]"
                      >
                        {{ equipment }}
                      </span>
                    </div>
                  </div>
                  <div class="flex items-center justify-between mb-4">
                    <div
                      class="flex items-center space-x-4 text-sm text-gray-500"
                    >
                      <span>项目规模：{{ item.scale }}</span>
                      <span>参与人数：{{ item.people }}</span>
                    </div>
                    <div class="text-right">
                      <span class="text-lg font-bold text-primary"
                        >¥{{ item.amount }}</span
                      >
                      <div class="text-xs text-gray-500">总投入</div>
                    </div>
                  </div>
                  <button
                    class="w-full bg-primary text-white py-3 !rounded-button hover:bg-secondary transition-colors whitespace-nowrap case-detail-btn group relative overflow-hidden"
                    id="view-case-detail"
                    @click.stop="goToCaseDetail(item.id)"
                  >
                    <span
                      class="flex items-center justify-center relative z-10"
                    >
                      查看详细案例
                      <i
                        class="ri-arrow-right-line ml-1 transition-transform group-hover:translate-x-1"
                      ></i>
                    </span>
                    <div
                      class="absolute inset-0 bg-secondary transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"
                    ></div>
                  </button>
                </div>
              </div>
            </div>
            <!-- 使用分页组件 -->
            <Pagination
              :total-count="totalCount"
              :page_current="page_current"
              :page_size="page_size"
              :visible-range="2"
              @page-change="handlePageChange"
            />
          </div>
        </div>
      </div>
    </main>
    <!-- 使用公共页脚组件 -->
    <Footer />
  </div>
</template>

<script>
import { Message } from "element-ui";
import Nav from "@/components/Nav.vue";
import Footer from "@/components/Footer.vue";
import Pagination from "@/components/Pagination.vue";
import api from "@/utils/api.js";

export default {
  name: "Caseshare",
  components: {
    Nav,
    Footer,
    Pagination,
  },
  data() {
    return {
      // 案例列表数据
      caseList: [],
      loading: false,

      // 分页信息
      totalCount: 0,
      page_current: 1,
      page_size: 12,

      // 筛选条件
      searchKeyword: "",
      selectedCategories: ["all"],
      selectedEquipments: ["all"],

      // 行业分类列表
      categoryList: [],

      // 排序相关
      sortBy: "default",
      showSortDropdown: false,
      sortOptions: {
        default: "默认排序",
        newest: "最新发布",
        popular: "热度排序",
      },

      // 视图模式
      viewMode: "grid",
    };
  },
  computed: {
    // 排序文本
    sortText() {
      return this.sortOptions[this.sortBy] || "默认排序";
    },
  },
  mounted() {
    this.getCategoryList(); // 获取行业分类列表
    this.getList(); // 初始加载案例列表
    // 添加文档点击事件监听器
    document.addEventListener("click", this.handleDocumentClick);
  },
  beforeUnmount() {
    // 移除文档点击事件监听器
    document.removeEventListener("click", this.handleDocumentClick);
  },
  methods: {
    // 处理文档点击，关闭下拉菜单
    handleDocumentClick(event) {
      const sortBtn = document.getElementById("sortBtn");
      const sortDropdown = document.getElementById("sortDropdown");

      if (
        sortBtn &&
        sortDropdown &&
        !sortBtn.contains(event.target) &&
        !sortDropdown.contains(event.target)
      ) {
        this.showSortDropdown = false;
      }
    },

    // 切换到网格视图
    switchToGridView() {
      this.viewMode = 'grid';
    },

    // 切换到列表视图
    switchToListView() {
      this.viewMode = 'list';
    },

    // 切换排序下拉菜单
    toggleSortDropdown() {
      this.showSortDropdown = !this.showSortDropdown;
    },

    // 处理排序变更
    handleSortChange(key) {
      this.sortBy = key;
      this.showSortDropdown = false;
      this.filterCases();
    },

    // 处理搜索
    handleSearch() {
      this.filterCases();
    },

    // 获取行业分类列表
    async getCategoryList() {
      try {
        this.loading = true;
        const response = await api.caseshare.getCategoryList();

        this.categoryList = response.data.categoryList || [];
      } catch (error) {
        console.error("获取行业分类列表失败:", error);
      } finally {
        this.loading = false;
      }
    },

    // 处理行业类型选择
    handleCategorySelect(e, category) {
      const isChecked = e.target.checked;

      // 全部分类的特殊处理
      if (category === "all") {
        if (isChecked) {
          // 选中全部行业，取消其他所有行业的选中状态
          this.selectedCategories = ["all"];
        } else {
          // 取消选中全部行业，保持其他行业的状态
          this.selectedCategories = this.selectedCategories.filter(
            (i) => i !== "all"
          );
        }
      } else {
        // 非全部行业的处理
        if (isChecked) {
          // 选中某个行业，取消全部行业的选中状态
          this.selectedCategories = this.selectedCategories.filter(
            (i) => i !== "all"
          );

          // 添加到已选中行业
          if (!this.selectedCategories.includes(category)) {
            this.selectedCategories.push(category);
          }
        } else {
          // 取消选中某个行业
          this.selectedCategories = this.selectedCategories.filter(
            (i) => i !== category
          );

          // 如果没有选中任何行业，自动选中全部行业
          if (this.selectedCategories.length === 0) {
            this.selectedCategories = ["all"];
          }
        }
      }

      // 筛选案例列表
      this.filterCases();
    },

    // 随机获取颜色类名，还有文字颜色
    getRandomColorClass() {
      const colors = [
        "bg-red-50 text-red-700",
        "bg-green-50 text-green-700",
        "bg-blue-50 text-blue-700",
        "bg-yellow-50 text-yellow-700",
        "bg-purple-50 text-purple-700",
        "bg-pink-50 text-pink-700",
      ];
      return colors[Math.floor(Math.random() * colors.length)];
    },

    // 跳转到案例详情页
    goToCaseDetail(caseId) {
      this.$router.push(`/caseshare/${caseId}`);
    },

    // 重置筛选条件
    resetFilters() {
      // 重置选中的行业
      this.selectedCategories = ["all"];

      // 重置选中的设备
      this.selectedEquipments = ["all"];

      // 重置搜索关键词
      this.searchKeyword = "";

      // 重置排序
      this.sortBy = "default";

      // 重新加载案例列表
      this.filterCases();
    },

    // 处理设备类型选择
    handleEquipmentSelect(e, equipment) {
      const isChecked = e.target.checked;

      // 全部设备的特殊处理
      if (equipment === "all") {
        if (isChecked) {
          // 选中全部设备，取消其他所有设备的选中状态
          this.selectedEquipments = ["all"];
        } else {
          // 取消选中全部设备，保持其他设备的状态
          this.selectedEquipments = this.selectedEquipments.filter(
            (e) => e !== "all"
          );
        }
      } else {
        // 非全部设备的处理
        if (isChecked) {
          // 选中某个设备，取消全部设备的选中状态
          this.selectedEquipments = this.selectedEquipments.filter(
            (e) => e !== "all"
          );

          // 添加到已选中设备
          if (!this.selectedEquipments.includes(equipment)) {
            this.selectedEquipments.push(equipment);
          }
        } else {
          // 取消选中某个设备
          this.selectedEquipments = this.selectedEquipments.filter(
            (e) => e !== equipment
          );

          // 如果没有选中任何设备，自动选中全部设备
          if (this.selectedEquipments.length === 0) {
            this.selectedEquipments = ["all"];
          }
        }
      }

      // 筛选案例列表
      this.filterCases();
    },

    // 切换到网格视图
    switchToGridView() {
      this.viewMode = "grid";
    },

    // 切换到列表视图
    switchToListView() {
      this.viewMode = "list";
    },

    // 加载案例列表
    async getList() {
      try {
        this.loading = true;

        // 构建请求参数
        const params = {
          page_current: this.page_current,
          page_size: this.page_size,
          sortBy: this.sortBy,
        };

        // 添加搜索关键词
        if (this.searchKeyword.trim()) {
          params.keyword = this.searchKeyword.trim();
        }

        // 添加行业筛选条件（如果不是全部行业）
        if (
          this.selectedCategories.length > 0 &&
          this.selectedCategories[0] !== "all"
        ) {
          params.category_ids = this.selectedCategories.join(",");
        }

        // 添加设备筛选条件（如果不是全部设备）
        if (
          this.selectedEquipments.length > 0 &&
          this.selectedEquipments[0] !== "all"
        ) {
          params.equipment_ids = this.selectedEquipments.join(",");
        }

        // 调用API获取案例列表（这里使用模拟数据，实际项目中应替换为真实API调用）
        const response = await api.caseshare.getList(params);
        this.caseList = response.data.resdata || [];
        this.totalCount = response.data.restotal || 0;
      } catch (error) {
        console.error("获取案例列表失败:", error);
        this.caseList = [];
        this.totalCount = 0;
      } finally {
        this.loading = false;
      }
    },

    // 筛选案例列表
    filterCases() {
      this.page_current = 1; // 重置到第一页
      this.getList(); // 重新加载案例列表
    },

    // 处理搜索
    handleSearch() {
      this.page_current = 1;
      this.getList();
    },

    // 切换排序下拉菜单
    toggleSortDropdown() {
      this.showSortDropdown = !this.showSortDropdown;
    },

    // 处理排序变化
    handleSortChange(sortBy) {
      this.sortBy = sortBy;
      this.page_current = 1;
      this.getList();
      this.showSortDropdown = false;
    },

    // 处理文档点击，关闭下拉菜单
    handleDocumentClick(event) {
      const sortBtn = document.getElementById("sortBtn");
      const sortDropdown = document.getElementById("sortDropdown");

      // 如果点击的不是排序按钮或下拉菜单本身，则关闭下拉菜单
      if (
        sortBtn &&
        sortDropdown &&
        !sortBtn.contains(event.target) &&
        !sortDropdown.contains(event.target)
      ) {
        this.showSortDropdown = false;
      }
    },

    // 重置筛选条件
    resetFilters() {
      // 重置选中的行业
      this.selectedCategories = ["all"];

      // 重置选中的设备
      this.selectedEquipments = ["all"];

      // 重置搜索关键词
      this.searchKeyword = "";

      // 重置排序
      this.sortBy = "default";

      // 重新加载案例列表
      this.filterCases();
    },

    // 处理分页变更
    handlePageChange(page) {
      this.page_current = page;
      this.getList();
    },

    // 查看案例详情
    viewCaseDetail(caseId) {
      // 在实际项目中跳转到案例详情页
      // this.$router.push(`/case/${caseId}`);
      Message.info("查看案例详情功能待实现");
    },
  },
};
</script>

<style scoped>
/* 容器样式 */
</style>



// WEBPACK FOOTER //
// src/pages/caseshare/Caseshare.vue